import * as React from 'react';
import { StyleSheet, css } from 'aphrodite/no-important';
import { useTheme } from '../../../../theming/useTheme';
import { footerSectionPadding } from './footer.utils';
import { TextLinkComponent, ThemedLinkStyle, generateLinkStyleFromTheme } from '../../../partials/link/TextLink';
import { MEDIA_BREAKPOINTS } from '../../../../mediaQueries.const';
import { H4, P, semanticCSS } from '../../../../typography/semanticTags';
import { LinkButton } from './LinkButton';
import { BaseUnit } from '../../../../theming/baseUnitDefinition';
import { LinkButtonDataProps } from '../utils';

const componentConfigurationKey = 'footerLinksSection';

interface Link {
  text: string;
  href: string;
}

interface NavigationLink {
  link: Link;
  children: NavigationLink | null;
}

export interface FooterLinksSectionBaseProps {
  title: string;
  buttons?: Array<LinkButtonDataProps>;
  links: NavigationLink[];
}

interface LinksSectionThemeProps {
  color: string;
  baseUnit: BaseUnit;
  idleColor: string;
  activeStateColor: string;
  hoverStateColor: string;
}

type LinksSectionProps = FooterLinksSectionBaseProps & ThemedLinkStyle;

const getStyle = (theme: LinksSectionThemeProps) => {
  const { baseUnit } = theme;
  const padding = footerSectionPadding(baseUnit);
  return StyleSheet.create({
    section: {
      ...padding,
    },
    title: {
      textAlign: 'center',
      color: theme.color,
    },
    gridWrapper: {
      display: 'flex',
      justifyContent: 'center',
    },
    linksWrapper: {
      display: 'inline-grid',
      marginTop: `${baseUnit * 0.5}px`,
      [MEDIA_BREAKPOINTS.large]: {
        gridTemplateColumns: 'auto auto auto auto',
        gridGap: `0 ${baseUnit * 4}px`,
      },
      [MEDIA_BREAKPOINTS.medium]: {
        gridTemplateColumns: 'auto auto auto',
        gridGap: `0 ${baseUnit * 4}px`,
      },
      [MEDIA_BREAKPOINTS.small]: {
        gridTemplateColumns: 'auto auto',
        gridGap: `0 ${baseUnit * 2}px`,
      },
    },
  });
};

export const FooterLinksSection: React.FunctionComponent<LinksSectionProps> = props => {
  const finalProps = { ...useTheme<LinksSectionThemeProps>(componentConfigurationKey), ...props };
  const { title, links, buttons, hoverStateColor, idleColor } = finalProps;
  const style = getStyle(finalProps);
  const linkStyle = generateLinkStyleFromTheme({
    ...finalProps,
  });

  return (
    <div className={css(style.section)}>
      <H4 styles={semanticCSS(style.title)}>{title}</H4>
      <div className={css(style.gridWrapper)}>
        <div className={css(style.linksWrapper)}>
          {links.map((navigationLink, index) => <TextLinkComponent key={index} href={navigationLink.link.href} linkStyle={linkStyle}><P>{navigationLink.link.text}</P></TextLinkComponent>)}
          {buttons ? buttons.map((button, index) => <LinkButton key={index} text={button.text} onClick={button.onClick} color={idleColor} hoverColor={hoverStateColor} />) : null}
        </div>
      </div>
    </div>
  );
};
